import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  chatFeedbackService,
  streamChat,
  type ChatCitation,
  type ChatOutcome,
} from "./chatService";
import styles from "./ChatWidget.module.css";

import { Button } from "@/components/Button";
import { SparkIcon, ThumbDownIcon, ThumbUpIcon, XIcon } from "@/components/icons";
import { useCurrentUser } from "@/hooks/useCurrentUser";

/** Anywhere in the app can open the assistant (optionally pre-filling a
 * question) by dispatching this event — e.g. the search no-results state. */
export const OPEN_CHAT_EVENT = "opshub:open-chat";

const WELCOME =
  "Ask me how any process works — in plain English. I'll answer from OpsHub and point you to the right section. Questions are logged anonymously.";

interface BotMessage {
  id: string;
  role: "bot";
  text: string;
  streaming: boolean;
  citations: ChatCitation[];
  questionLogId: string | null;
  outcome: ChatOutcome | null;
  feedback: "up" | "down" | null;
}
interface UserMessage {
  id: string;
  role: "user";
  text: string;
}
type Message = UserMessage | BotMessage;

let nextId = 0;
function messageId(): string {
  nextId += 1;
  return `m${nextId}`;
}

/**
 * The OpsHub assistant: a slide-in panel (prototype .chat-panel) triggered
 * from a header toggle. Hidden entirely for anyone without chat.use. Streams
 * the answer via fetch + SSE (chatService.ts), renders numbered citations as
 * "go to section" links (with a snippet in search mode, when no key is set), and offers thumbs feedback (a thumbs-down fires the
 * manager alert email server-side — see docs/build-plan.md).
 */
export function ChatWidget() {
  const { permissions, csrfToken } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Deep-open from elsewhere in the app (e.g. "Ask the assistant" on an
  // empty search) — optionally pre-filling the question.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const question = (e as CustomEvent<{ question?: string }>).detail?.question;
      if (question) setInput(question);
      setOpen(true);
    };
    window.addEventListener(OPEN_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages]);

  if (!permissions.includes("chat.use")) return null;

  async function ask(question: string): Promise<void> {
    const trimmed = question.trim();
    if (!trimmed || busy) return;
    setInput("");
    setBusy(true);

    const botId = messageId();
    setMessages((prev) => [
      ...prev,
      { id: messageId(), role: "user", text: trimmed },
      {
        id: botId,
        role: "bot",
        text: "",
        streaming: true,
        citations: [],
        questionLogId: null,
        outcome: null,
        feedback: null,
      },
    ]);

    function updateBot(patch: Partial<BotMessage>): void {
      setMessages((prev) =>
        prev.map((m) => (m.id === botId && m.role === "bot" ? { ...m, ...patch } : m))
      );
    }

    await streamChat(trimmed, csrfToken ?? "", {
      onDelta: (text) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === botId && m.role === "bot" ? { ...m, text: m.text + text } : m))
        );
      },
      onDone: (event) => {
        updateBot({
          streaming: false,
          citations: event.citations,
          questionLogId: event.questionLogId,
          outcome: event.outcome,
        });
      },
      onError: (message) => {
        updateBot({ streaming: false, text: message, outcome: "error" });
      },
    });
    setBusy(false);
  }

  async function giveFeedback(
    botId: string,
    questionLogId: string,
    thumbsUp: boolean
  ): Promise<void> {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === botId && m.role === "bot" ? { ...m, feedback: thumbsUp ? "up" : "down" } : m
      )
    );
    try {
      await chatFeedbackService.send(questionLogId, thumbsUp, csrfToken ?? "");
    } catch {
      // Feedback is a nicety, not a critical path — a failed POST just means
      // the chosen state on screen doesn't reach the server; nothing to
      // recover from client-side.
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        className={styles.toggle}
        aria-label="Ask OpsHub"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <SparkIcon className={styles.toggleIcon} />
        <span className={styles.toggleLabel}> Ask OpsHub</span>
      </Button>

      <aside
        className={open ? `${styles.panel} ${styles.open}` : styles.panel}
        aria-label="OpsHub assistant"
        aria-hidden={!open}
      >
        <div className={styles.head}>
          <div>
            <h2 className={styles.title}>OpsHub assistant</h2>
            <div className={styles.subtitle}>Answers only from OpsHub and its linked documents</div>
          </div>
          <button
            type="button"
            className={styles.close}
            aria-label="Close assistant"
            onClick={() => setOpen(false)}
          >
            <XIcon />
          </button>
        </div>

        <div className={styles.log} ref={logRef}>
          <div className={`${styles.msg} ${styles.bot}`}>{WELCOME}</div>
          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className={`${styles.msg} ${styles.user}`}>
                {m.text}
              </div>
            ) : (
              <div key={m.id} className={`${styles.msg} ${styles.bot}`}>
                {m.text || (m.streaming ? "…" : "")}
                {!m.streaming && m.outcome === "fallback" && (
                  <div className={styles.fallbackNote}>
                    Unanswered questions are logged (anonymously) so OpsHub can be improved.
                  </div>
                )}
                {!m.streaming && m.citations.length > 0 && (
                  <div className={styles.citations}>
                    {m.citations.map((c, i) => (
                      <Link
                        key={`${c.pageId}-${i}`}
                        to={`/manual/${c.sectionSlug}/${c.pageSlug}${c.anchor ? `#${c.anchor}` : ""}`}
                        className={styles.citation}
                      >
                        <span className={styles.citationNum}>[{i + 1}]</span>{" "}
                        <span className={styles.citationBody}>
                          {c.headingPath.length > 0
                            ? `${c.title} › ${c.headingPath[c.headingPath.length - 1]}`
                            : c.title}
                          {c.snippet && <span className={styles.citationSnippet}>{c.snippet}</span>}
                        </span>
                        <span className={styles.citationArrow}>→</span>
                      </Link>
                    ))}
                  </div>
                )}
                {!m.streaming &&
                  m.questionLogId &&
                  (m.outcome === "answered" || m.outcome === "search") && (
                    <div className={styles.feedback}>
                      <span>Did this answer your question?</span>
                      <button
                        type="button"
                        aria-label="Yes, this answered my question"
                        className={m.feedback === "up" ? styles.chosen : ""}
                        onClick={() => giveFeedback(m.id, m.questionLogId!, true)}
                        disabled={m.feedback !== null}
                      >
                        <ThumbUpIcon />
                      </button>
                      <button
                        type="button"
                        aria-label="No, this didn't answer my question"
                        className={m.feedback === "down" ? styles.chosen : ""}
                        onClick={() => giveFeedback(m.id, m.questionLogId!, false)}
                        disabled={m.feedback !== null}
                      >
                        <ThumbDownIcon />
                      </button>
                      {m.feedback && (
                        <span className={styles.feedbackNote}>
                          {m.feedback === "down"
                            ? "Thanks — the content owner gets an email alert about this."
                            : "Thanks — feedback recorded."}
                        </span>
                      )}
                    </div>
                  )}
              </div>
            )
          )}
        </div>

        <form
          className={styles.inputRow}
          onSubmit={(e) => {
            e.preventDefault();
            void ask(input);
          }}
        >
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder="e.g. How do I set up a new project folder?"
            autoComplete="off"
            value={input}
            disabled={busy}
            onChange={(e) => setInput(e.target.value)}
          />
          <Button variant="primary" type="submit" disabled={busy || !input.trim()}>
            Ask
          </Button>
        </form>
        <div className={styles.disclaimer}>
          The assistant is a way to find information in OpsHub — it isn&rsquo;t professional advice.
          Staff remain responsible for following the approved process.
        </div>
      </aside>
    </>
  );
}
